import React, { useEffect, useRef } from "react";
import { Button } from "@progress/kendo-react-buttons";
import {
  Field,
  Form,
  FormElement,
  FormRenderProps,
} from "@progress/kendo-react-form";
import { GridLayout, GridLayoutItem } from "@progress/kendo-react-layout";
import { requiredValidator } from "../../components/formFields/CommonValidator";
import FormTextField from "../../components/formFields/FormTextField";
import ButtonWithLoading from "../../components/common/ButtonWithLoading";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { LoadingPanel } from "../../components/layout/Loading";
import { findIndex } from "lodash";
import FormSelectionField from "../../components/formFields/FormSelectionField";
import { clearCityDetails } from "./citySlice";
import { getAllActiveCountries } from "../country/services/country.services";
import { getAllActiveStates } from "../state/services/state.services";
import {
  createCity,
  getAllCities,
  getCityByID,
  updateCity,
} from "./services/city.services";
import { IState } from "../state/stateModel";
import { ICountry } from "../country/countryModel";
import { Typography } from "@progress/kendo-react-common";
import { RxCross1 } from "react-icons/rx";
import { STATUSARRAY } from "../../_contstants/common";
import { closed } from "../../components/drawer/drawerSlice";

interface StateChangeWatcherProps {
  formRenderProps: FormRenderProps;
  StateList: IState[];
}

const StateChangeWatcher: React.FC<StateChangeWatcherProps> = ({
  StateList,
  formRenderProps,
}) => {
  const state_ID = formRenderProps.valueGetter("StateID");

  useEffect(() => {
    const StateID = state_ID ? state_ID : undefined;
    const stateIndex = findIndex(StateList, { ID: StateID });
    if (stateIndex > -1) {
      const state = StateList[stateIndex];
      formRenderProps.onChange("CountryID", { value: state.CountryID });
    } else {
      formRenderProps.onChange("StateID", { value: "" });
      formRenderProps.onChange("CountryID", { value: "" });
    }
  }, [state_ID]);
  return null;
};

const CreateCity: React.FC = () => {
  const dispatch = useAppDispatch();
  const gridRef = useRef<any>(null);

  const CountryList = useAppSelector((state) => state.country.CountryList);
  const StateList = useAppSelector((state) => state.state.StateList);
  const CityDetail = useAppSelector((state) => state.city.CityDetail);
  const CityGUID = useAppSelector((state) => state.drawer.data);
  const formLoading = useAppSelector((state) => state.city.formLoading);

  useEffect(() => {
    dispatch(getAllActiveCountries());
    dispatch(getAllActiveStates());
    return () => {
      dispatch(clearCityDetails());
      dispatch(closed());
    };
  }, []);

  useEffect(() => {
    if (CityGUID) {
      dispatch(getCityByID(CityGUID));
    }
  }, [CityGUID]);

  const handleSubmit = async (values: any) => {
    try {
      if (!CityGUID) {
        const insertPayload = {
          CityName: values.CityName,
          StateID: values.StateID ? +values.StateID : null,
          CountryID: values.CountryID ? +values.CountryID : null,
          IsActive: values.IsActive === 1,
        };
        const response = await dispatch(createCity(insertPayload));
        if (response?.payload?.Status === 201) {
          dispatch(closed());
          dispatch(clearCityDetails());
          dispatch(getAllCities());
        }
      } else {
        const editPayload = {
          CityGUID: CityGUID,
          CityName: values.CityName,
          StateID: values.StateID ? +values.StateID : null,
          CountryID: values.CountryID ? +values.CountryID : null,
          IsActive: values.IsActive === 1,
        };
        const response = await dispatch(updateCity(editPayload));
        if (response?.payload?.Status === 200) {
          dispatch(closed());
          dispatch(clearCityDetails());
          dispatch(getAllCities());
        }
      }
    } catch (error) {
      console.error("Error in handleSubmit:", error);
      throw error;
    }
  };

  const handleFormClose = () => {
    dispatch(closed());
  };

  if (formLoading) return <LoadingPanel gridRef={gridRef} />;

  return (
    <Form
      onSubmit={handleSubmit}
      initialValues={CityDetail}
      render={(formRenderProps: FormRenderProps) => (
        <FormElement style={{ width: "100%" }}>
          <GridLayout
            style={{
              display: "flex",
              flexWrap: "wrap",
            }}
            gap={{ rows: 10 }}
            cols={[{ width: "100%" }]}
          >
            <GridLayoutItem className="drawer-header">
              <Typography.h4 className="m-0">
                {CityGUID ? "Update City" : "Create City"}
              </Typography.h4>
              <Button
                type="button"
                fillMode={"flat"}
                themeColor={"dark"}
                style={{ marginRight: 4 }}
                onClick={() => dispatch(closed())}
              >
                <RxCross1 />
              </Button>
            </GridLayoutItem>
            <GridLayoutItem style={{ width: "100%" }}>
              <Field
                name="CityName"
                label="City Name"
                placeholder="i.e. Ahmedabad"
                component={FormTextField}
                validator={requiredValidator} astrike={true}
              />
            </GridLayoutItem>
            <GridLayoutItem style={{ width: "100%" }}>
              <Field
                id={"StateID"}
                name={"StateID"}
                label={"State Name"}
                placeholder="i.e. Gujarat"
                component={FormSelectionField}
                validator={requiredValidator} astrike={true}
                options={StateList?.map((state: IState) => {
                  return {
                    value: state?.ID,
                    label: state?.StateName,
                  };
                })}
              />
            </GridLayoutItem>
            <GridLayoutItem style={{ width: "100%" }}>
              <Field
                id={"CountryID"}
                name={"CountryID"}
                label={"Country Name"}
                placeholder="i.e. India"
                component={FormSelectionField}
                // validator={requiredValidator}
                disabled={true}
                options={CountryList.map((country: ICountry) => {
                  return {
                    value: country?.ID,
                    label: country?.CountryName,
                  };
                })}
              />
            </GridLayoutItem>
            <GridLayoutItem style={{ width: "100%" }}>
              <Field
                id={"IsActive"}
                name={"IsActive"}
                label={"Status"}
                placeholder="Status"
                component={FormSelectionField}
                validator={requiredValidator} astrike={true}
                options={STATUSARRAY?.map((status: any) => {
                  return {
                    value: status.value,
                    label: status.label,
                  };
                })}
              />
            </GridLayoutItem>
            <GridLayoutItem
              colSpan={3}
              style={{
                marginTop: 20,
                width: "100%",
              }}
            >
              <ButtonWithLoading
                label={CityGUID ? "Update" : "Create"}
                type="submit"
                disabled={!formRenderProps.allowSubmit || formLoading}
                loading={formLoading}
              />
              <Button
                type="button"
                fillMode={"outline"}
                themeColor={"primary"}
                style={{ marginLeft: 4 }}
                onClick={handleFormClose}
              >
                Cancel
              </Button>
            </GridLayoutItem>
            <StateChangeWatcher
              StateList={StateList}
              formRenderProps={formRenderProps}
            />
          </GridLayout>
        </FormElement>
      )}
    />
  );
};

export default CreateCity;
